import { supabase, getSupabaseAdmin } from '../../lib/supabase';
import { User } from '../../store'; // Menggunakan tipe User dari store existing
import { LogService } from './logs';

// Error interface for typed error handling
interface AppError extends Error {
  status?: number;
  statusCode?: number;
  code?: string;
  original?: unknown;
  originalError?: unknown;
}

// Supabase Auth User type
interface SupabaseUser {
  id: string;
  email?: string;
  user_metadata?: {
    full_name?: string;
    role?: string;
    avatar_url?: string;
    picture?: string;
  };
}

// Auth result type from Supabase
interface AuthResult {
  data?: { user?: SupabaseUser | null; session?: unknown };
  error?: { message?: string; status?: number; code?: string } | null;
}

// Profile data from database
interface ProfileData {
  full_name?: string;
  role?: string;
  is_profile_completed?: boolean;
  avatar_url?: string;
  is_profile_locked?: boolean;
  is_active?: boolean;
}

export const authService = {
  /**
   * Register user baru
   */
  async register(
    email: string,
    password: string,
    fullName: string,
    role: string = 'student',
    retries: number = 3,
  ) {
    let lastError: AppError | undefined;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        // Create signup promise
        const signUpPromise = supabase.auth.signUp({
          email: email.trim().toLowerCase(),
          password,
          options: {
            data: {
              full_name: fullName.trim(),
              role: role,
            },
            emailRedirectTo: `${window.location.origin}/dashboard`,
          },
        });

        // Wrap with timeout (65 seconds to be slightly longer than fetch timeout)
        let timeoutId: NodeJS.Timeout;
        const timeoutPromise = new Promise<never>((_, reject) => {
          timeoutId = setTimeout(() => {
            reject(new Error('Signup timeout - server tidak merespons. Silakan coba lagi.'));
          }, 65000);
        });

        let result: AuthResult;
        try {
          result = (await Promise.race([signUpPromise, timeoutPromise])) as AuthResult;
          clearTimeout(timeoutId!);
        } catch (timeoutError: unknown) {
          clearTimeout(timeoutId!);
          const error = timeoutError as Error;
          // If timeout, treat as retryable error
          if (error.message?.includes('timeout')) {
            lastError = error;
            if (attempt < retries) {
              const delay = 2000 * Math.pow(2, attempt);
              await new Promise((resolve) => setTimeout(resolve, delay));
              continue;
            }
            throw timeoutError;
          }
          throw timeoutError;
        }

        const { data, error } = result;

        if (error) {
          // Don't retry on client errors (4xx) - these are permanent failures
          if (error.status && error.status >= 400 && error.status < 500) {
            // Map common error codes to user-friendly messages
            if (error.status === 422) {
              const friendlyError: AppError = new Error(
                'Email atau password tidak valid. Pastikan email valid dan password memenuhi syarat.',
              );
              friendlyError.status = error.status;
              throw friendlyError;
            } else if (error.status === 409 || error.message?.includes('already registered')) {
              const friendlyError: AppError = new Error(
                'Email sudah terdaftar. Silakan login atau gunakan email lain.',
              );
              friendlyError.status = error.status;
              throw friendlyError;
            }
            throw error;
          }

          // For server errors (5xx) or network errors, retry with longer delays
          // 503 Service Unavailable needs longer backoff - usually means GoTrue service is down/overloaded
          const authError = new Error(error.message || 'Auth error') as AppError;
          authError.status = error.status;
          authError.code = error.code;
          lastError = authError;
          if (attempt < retries) {
            // Longer delays for server errors: 5s, 10s, 20s for 503 (service unavailable), or 3s, 6s, 12s for others
            // 503 usually means backend service is down, so we wait longer
            const baseDelay = error.status === 503 ? 5000 : 3000;
            const delay = baseDelay * Math.pow(2, attempt);
            await new Promise((resolve) => setTimeout(resolve, delay));
            continue;
          }
          throw error;
        }

        // Verify profile was created (wait a bit for trigger to complete)
        if (data.user) {
          // Wait for trigger to create profile (non-blocking check)
          setTimeout(async () => {
            try {
              const { data: profile, error: profileError } = await supabase
                .from('profiles')
                .select('id')
                .eq('id', data.user.id)
                .single();

              if (profileError || !profile) {
                // Try to create profile manually as fallback
                await supabase.from('profiles').insert({
                  id: data.user.id,
                  email: data.user.email,
                  full_name: fullName.trim(),
                  role: role,
                  is_profile_completed: false,
                  is_profile_locked: false,
                  is_active: true,
                });
              }
            } catch {
              // Silent fail - profile creation is non-critical
            }
          }, 500);

          // Log asynchronously (non-blocking) to prevent timeout
          LogService.createLog(
            'CREATE',
            'USER',
            data.user.id,
            { email, role, name: fullName },
            { id: data.user.id, email: data.user.email },
          ).catch(() => {
            // Silent fail - logging is non-critical
          });
        }

        return data;
      } catch (error) {
        lastError = error as AppError;

        // Check if it's a timeout or network error
        const isRetryableError =
          error.message?.includes('timeout') ||
          error.message?.includes('Gateway Timeout') ||
          error.message?.includes('Service Unavailable') ||
          error.message?.includes('503') ||
          error.message?.includes('504') ||
          error.message?.includes('Network') ||
          error.name === 'AuthRetryableFetchError' ||
          error.name === 'TimeoutError' ||
          (error.status && error.status >= 500);

        if (isRetryableError && attempt < retries) {
          // Longer delays for server errors: 5s, 10s, 20s for 503, or 3s, 6s, 12s for others
          const baseDelay = error.status === 503 || error.message?.includes('503') ? 5000 : 3000;
          const delay = baseDelay * Math.pow(2, attempt);
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        } else if (!isRetryableError) {
          // For non-retryable errors, throw immediately
          throw error;
        }
      }
    }

    // If we exhausted all retries, throw the last error with a friendly message
    if (lastError) {
      let friendlyMessage =
        'Server sedang sibuk atau koneksi timeout. Silakan coba lagi dalam beberapa saat.';

      // Specific messages for different error types
      if (
        lastError.status === 503 ||
        lastError.message?.includes('503') ||
        lastError.message?.includes('Service Unavailable')
      ) {
        friendlyMessage =
          'Server Supabase sedang tidak tersedia (503 Service Unavailable). Ini biasanya berarti backend service sedang down atau overloaded. Silakan tunggu beberapa menit dan coba lagi. Jika masalah berlanjut, cek status Supabase di dashboard atau hubungi administrator.';
      } else if (
        lastError.status === 504 ||
        lastError.message?.includes('504') ||
        lastError.message?.includes('Gateway Timeout')
      ) {
        friendlyMessage =
          'Server tidak merespons (Gateway Timeout). Server sedang lambat, silakan coba lagi dalam beberapa saat.';
      } else if (lastError.message?.includes('timeout')) {
        friendlyMessage = 'Koneksi timeout. Server sedang lambat, silakan coba lagi.';
      }

      const friendlyError: AppError = new Error(friendlyMessage);
      friendlyError.originalError = lastError;
      friendlyError.status = lastError?.status || 504;
      throw friendlyError;
    }

    throw new Error('Gagal mendaftar setelah beberapa kali percobaan. Silakan coba lagi.');
  },

  /**
   * Login user
   */
  async login(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;

    // Check if user account is active
    if (data.user) {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('is_active')
        .eq('id', data.user.id)
        .single();

      if (profileError) {
        // If we can't check status, allow login but log the error silently
      } else if (profile && profile.is_active === false) {
        // Sign out the user immediately
        await supabase.auth.signOut();
        throw new Error(
          'Akun Anda telah dinonaktifkan. Silakan hubungi administrator untuk mengaktifkan kembali akun Anda.',
        );
      }
    }

    // Log activity
    await LogService.createLog(
      'LOGIN',
      'AUTH',
      data.user.id,
      { email },
      { id: data.user.id, email: data.user.email },
    );

    return data;
  },

  /**
   * Login with Google
   */
  async loginWithGoogle(redirectTo?: string) {
    // Use a dedicated callback route to handle OAuth response cleanly
    const callbackUrl = redirectTo || `${window.location.origin}/auth/callback`;
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: callbackUrl,
        // PKCE flow is recommended; client configured with flowType: 'pkce'
      },
    });

    if (error) throw error;
    return data;
  },

  /**
   * Logout user
   */
  async logout() {
    // Log activity before signing out
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        await LogService.createLog(
          'LOGOUT',
          'AUTH',
          undefined,
          { method: 'manual' },
          { id: user.id, email: user.email },
        );
      }
    } catch {
      // Silent fail - logout log is non-critical
    }

    // Sign out from Supabase (this clears auth session)
    const { error } = await supabase.auth.signOut();
    if (error) throw error;

    // Clear any remaining localStorage items (redundant but ensures cleanup)
    // Note: Zustand persist will be cleared by store.logout()
    // Additional cleanup is handled in store.ts logout function
  },

  /**
   * Mendapatkan session user saat ini
   */
  async getSession() {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    return data.session;
  },

  /**
   * Mendapatkan profil user dari tabel 'profiles'
   * @param existingUser Optional: Supabase user object if already fetched
   * Optimized dengan timeout dan error handling yang lebih baik untuk Cloudflare
   */
  async getCurrentUser(existingUser?: SupabaseUser) {
    let user = existingUser;

    if (!user) {
      // Try to get the supabase auth user. We call this safely and treat 401/403
      // as "no session" instead of bubbling an error, to avoid noisy console
      // errors when a client has no active session.
      try {
        const getUserPromise = supabase.auth.getUser();
        const timeoutPromise = new Promise((resolve) =>
          setTimeout(() => resolve({ data: { user: null }, error: { message: 'timeout' } }), 5000),
        );

        const result = (await Promise.race([getUserPromise, timeoutPromise])) as AuthResult;
        // If the call timed out, result.error.message === 'timeout' and we'll continue
        user = result?.data?.user;

        // If there's an error other than timeout, only throw when it's unexpected.
        if (result?.error && !result?.error?.message?.includes('timeout')) {
          const err = result.error as AppError;
          // Treat 401/403 as no-session (return null) instead of throwing
          if (
            err.status === 401 ||
            err.status === 403 ||
            (err.message && /(401|403)/.test(String(err.message)))
          ) {
            user = null;
          } else {
            throw err;
          }
        }
      } catch {
        // If the runtime throws (network/abort), treat as no-session and continue.
        // This prevents an auth-check from blocking UI flows like profile completion.
        user = null;
      }
    }

    if (!user) return null;

    // Fetch profile data from 'profiles' table dengan timeout
    // Cloudflare edge bisa membantu cache response jika di-configure
    const profilePromise = supabase.from('profiles').select('*').eq('id', user.id).single();

    const profileTimeoutPromise = new Promise((resolve) =>
      setTimeout(() => resolve({ data: null, error: { code: 'TIMEOUT' } }), 5000),
    );

    const result = (await Promise.race([profilePromise, profileTimeoutPromise])) as {
      data: ProfileData | null;
      error?: { code?: string };
    };
    const { data: profile, error } = result;

    if (error && error.code !== 'PGRST116' && error.code !== 'TIMEOUT') {
      // Silent fail - profile fetch error is handled gracefully
      // Timeout juga di-handle gracefully untuk mencegah blocking
    }

    // Check if user account is active
    if (profile && profile.is_active === false) {
      // Sign out inactive user
      await supabase.auth.signOut();
      throw new Error(
        'Akun Anda telah dinonaktifkan. Silakan hubungi administrator untuk mengaktifkan kembali akun Anda.',
      );
    }

    // Mapping ke format User aplikasi
    // Prioritize profile table data, fallback to metadata
    const roleValue = profile?.role || user.user_metadata?.role || 'student';
    const appUser: User = {
      id: user.id,
      email: user.email || '',
      name: profile?.full_name || user.user_metadata?.full_name || 'User',
      role: roleValue as User['role'],
      isProfileCompleted: profile?.is_profile_completed || false,
      avatar_url:
        profile?.avatar_url || user.user_metadata?.avatar_url || user.user_metadata?.picture,
      isProfileLocked: profile?.is_profile_locked || false,
      is_active: profile?.is_active !== false, // Default to true if not set
    };

    return appUser;
  },

  /**
   * Update user profile
   */
  async updateProfile(
    id: string,
    updates:
      | {
          full_name?: string;
          is_profile_completed?: boolean;
          avatar_url?: string;
          is_profile_locked?: boolean;
        }
      | Record<string, unknown>,
  ) {
    // Use upsert to atomically insert or update the profile row based on `id`.
    // This avoids the 406/PGRST116 case when update matches 0 rows.
    const payload = { id, ...updates } as Record<string, unknown>;
    const { data, error } = await supabase
      .from('profiles')
      .upsert(payload, { onConflict: 'id' })
      .select()
      .single();

    if (error) {
      // Provide additional context for common permission issues
      const appError = error as unknown as AppError;
      const status = appError?.status || appError?.statusCode || null;
      if (
        status === 403 ||
        String(error?.message || '')
          .toLowerCase()
          .includes('permission')
      ) {
        const e: AppError = new Error(
          'Gagal memperbarui profil: izin ditolak. Periksa RLS/policy tabel `profiles`.',
        );
        e.original = error;
        throw e;
      }
      throw error;
    }

    return data;
  },

  /**
   * Get all users (admin only)
   */
  async getAllUsers() {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  /**
   * Create new user (admin only)
   */
  async createUser(userData: {
    full_name: string;
    email: string;
    role: string;
    avatar_url?: string;
  }) {
    // First create auth user
    const tempPassword = Math.random().toString(36).slice(-12) + 'Temp123!'; // Generate temp password

    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: userData.email,
      password: tempPassword,
      options: {
        data: {
          full_name: userData.full_name,
          role: userData.role,
          avatar_url: userData.avatar_url,
        },
      },
    });

    if (authError) throw authError;

    // Wait a moment for profile to be created by trigger
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Update profile with additional data if needed
    if (authData.user) {
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          full_name: userData.full_name,
          role: userData.role,
          avatar_url: userData.avatar_url,
        })
        .eq('id', authData.user.id);

      if (profileError) throw profileError;

      await LogService.createLog('CREATE', 'USER', authData.user.id, userData, {
        id: authData.user.id,
        email: userData.email,
      });
    }

    return authData.user;
  },

  /**
   * Delete user (admin only) - uses service role key
   */
  async deleteUser(userId: string) {
    // Get user data before deletion for logging
    const { data: userData } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    // Delete from profiles table
    const { error: profileError } = await supabase.from('profiles').delete().eq('id', userId);

    if (profileError) throw profileError;

    // Delete from auth.users using service role key (server-only)
    const supabaseAdmin = getSupabaseAdmin();
    if (!supabaseAdmin) {
      // If we're running in the browser, we cannot delete auth users.
      throw new Error(
        'Operation not allowed in client. Please perform deletion from server-side admin endpoint.',
      );
    }

    const { error: authError } = await supabaseAdmin.auth.admin.deleteUser(userId);

    if (authError) {
      // Silent fail - profile already deleted, auth user deletion is secondary
    }

    if (userData) {
      await LogService.createLog('DELETE', 'USER', userId, userData, { id: userId });
    }
  },

  /**
   * Update user (admin only)
   */
  async updateUser(
    userId: string,
    updates: {
      full_name?: string;
      email?: string;
      role?: string;
      avatar_url?: string;
      is_active?: boolean;
    },
  ) {
    // Use browser-safe supabase client for updating profiles table.
    // Note: this requires appropriate RLS/policies so that an admin user can update other profiles.
    const { error } = await supabase
      .from('profiles')
      .update({
        full_name: updates.full_name,
        email: updates.email,
        role: updates.role,
        avatar_url: updates.avatar_url,
        is_active: updates.is_active,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId);

    if (error) throw error;

    // Get updated data for return (use same client)
    const { data, error: selectError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (selectError) throw selectError;

    await LogService.createLog('UPDATE', 'USER', userId, updates, { id: userId });

    return data;
  },

  /**
   * Toggle user active status (admin only)
   */
  async toggleUserActive(userId: string, isActive: boolean) {
    // Use browser-safe supabase client for toggling active flag on profiles.
    const { error } = await supabase
      .from('profiles')
      .update({
        is_active: isActive,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId);

    if (error) throw error;

    // Get updated data for return
    const { data: updatedData, error: selectError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (selectError) throw selectError;

    await LogService.createLog('UPDATE', 'USER', userId, { is_active: isActive }, { id: userId });

    return updatedData;
  },
};
